import { contentDb, type PageItem, type PageRow } from '@/db/content';
import { getExpectedCacheGeneration } from '@/db/contentCache';
import type { PagesApiResponse } from './types';
import { buildConsolidatedRowFromListingItem } from './consolidatedIdb';

/** Sort key for tree order (canonical listing materialized path). */
function listingItemPath(item: PageItem): string {
  return typeof item.meta.path === 'string' ? item.meta.path : '';
}

/** Canonical listing rows from consolidated IDB rows (sorted by materialized path). */
export function pageItemsFromConsolidatedRows(rows: PageRow[]): PageItem[] {
  const sorted = [...rows].sort((a, b) =>
    listingItemPath(a.listingItem).localeCompare(listingItemPath(b.listingItem))
  );
  return sorted.map((r) => r.listingItem);
}

/**
 * When we receive a new page listing API response (200): upsert one IndexedDB row per
 * translation_key. Then update the Site row (etag, navigation, listing snapshot).
 */
export async function applyListingResponse(
  data: PagesApiResponse,
  etag: string | null
): Promise<void> {
  const gen = getExpectedCacheGeneration();
  for (const item of data.items) {
    const tk = item.meta.translation_key;
    if (!tk) continue;
    let existing: PageRow | undefined;
    try {
      existing = await contentDb.page.get(tk);
    } catch {
      existing = undefined;
    }
    const row = buildConsolidatedRowFromListingItem(item, existing);
    try {
      await contentDb.page.put(row);
    } catch {
      // Ignore cache write failures
    }
  }
  try {
    await contentDb.site.put({
      id: 'default',
      etag,
      navigation: data.navigation,
      listingItems: data.items,
      siteClientGeneration: gen,
    });
  } catch {
    // Ignore cache write failures
  }
}

/** Build listing from IndexedDB (offline/cache bootstrap). */
export async function getListingFromPageTable(): Promise<PagesApiResponse | null> {
  let site;
  try {
    site = await contentDb.site.get('default');
  } catch {
    site = undefined;
  }
  if (site?.listingItems?.length) {
    return {
      meta: { total_count: site.listingItems.length },
      items: site.listingItems,
      navigation: site?.navigation,
    };
  }
  let rows: PageRow[] = [];
  try {
    rows = await contentDb.page.toArray();
  } catch {
    return null;
  }
  if (rows.length === 0) return null;
  const sorted = [...rows].sort((a, b) =>
    listingItemPath(a.listingItem).localeCompare(listingItemPath(b.listingItem))
  );
  return {
    meta: { total_count: sorted.length },
    items: sorted.map((r) => r.listingItem),
    navigation: site?.navigation,
  };
}
